import type { MembershipBillingCycle } from '@goelectric/shared-types';
import {
  brandColors,
  darkColors,
  neutralColors,
  semanticColors,
} from '@goelectric/ui-components';
import type { StatusIconKey } from '@goelectric/ui-components';
import { StatusBadgeShell, StatusIcon, useStatusColor } from '@/components/StatusBadgeShell';
import {
  BILLING_CYCLE_DESCRIPTIONS,
  BILLING_CYCLE_LABELS,
  MEMBERSHIP_STATUS_LABELS,
  membershipStatusDescription,
} from '../utils/format';

interface BadgeMeta {
  color: string;
  colorDark: string;
  iconKey: StatusIconKey;
}

/** Color e icono por estado: el estado nunca depende solo del color (WCAG 2.2 AA). */
const ACTIVE_META: BadgeMeta = {
  color: semanticColors.success,
  colorDark: darkColors.success,
  iconKey: 'check',
};

const INACTIVE_META: BadgeMeta = {
  color: neutralColors.textMuted,
  colorDark: darkColors.textMuted,
  iconKey: 'offline',
};

const CYCLE_META: BadgeMeta = {
  color: brandColors.info,
  colorDark: darkColors.info,
  iconKey: 'info',
};

/** Estado de la membresía con color + icono + texto. */
export function MembershipStatusBadge({
  active,
  forceDark = false,
}: {
  active: boolean;
  forceDark?: boolean;
}) {
  const meta = active ? ACTIVE_META : INACTIVE_META;
  const label = active ? MEMBERSHIP_STATUS_LABELS.ACTIVE : MEMBERSHIP_STATUS_LABELS.INACTIVE;
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={label}
      title={`${label}: ${membershipStatusDescription(active)}`}
    />
  );
}

/** Ciclo de facturación con color + icono + texto. */
export function BillingCycleBadge({
  cycle,
  forceDark = false,
}: {
  cycle: MembershipBillingCycle;
  forceDark?: boolean;
}) {
  const label = BILLING_CYCLE_LABELS[cycle];
  const color = useStatusColor(CYCLE_META.color, CYCLE_META.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={CYCLE_META.iconKey} />}
      label={label}
      title={`${label}: ${BILLING_CYCLE_DESCRIPTIONS[cycle]}`}
    />
  );
}
